/**
 * Semantic linter rule for SplineIK3D: Godot's own configuration warning "Detecting settings with
 * no Path3D set!" (spline_ik_3d.cpp:109-118). `_process_ik` skips a setting whose NodePath resolves
 * to nothing (spline_ik_3d.cpp:313-316), so that chain is never posed. Godot omits the empty
 * default, so only `setting_count` counts the absences: a rule, not a validator (ADR-0032).
 */
import { ruleRegistry } from '../../../../linter/RuleRegistry.js';
import { descendsFromClass } from '../../../../godot/classdb/classBaseTypes.js';
import { indexedElements } from '../../../../godot/classdb/indexedKey.js';
import { nodePathLiteral } from '../../../../godot/variant/variantParser.js';
import { ruleCount } from '../../../../godot/variant/intSlots.js';
import { unsatisfiedIndices } from '../../../../linter/reportedIndices.js';
import { resolveSplineSettingLeaf } from './linterParser.js';
import { armDiagnostic, armEmits, groundedArm, reportArm, } from '../../../../linter/ruleArms.js';
const arms = {
    settingWithoutPath3D: groundedArm('splineik3d-setting-without-path-3d', { kind: 'configuration-warning' }),
};
/** `NodePath("")` and a bare `""`, the two spellings of the unset path. */
function isUnsetPath(raw) {
    return nodePathLiteral(raw) === '' || raw === '""';
}
function checkSplineIK3D(context) {
    const { node } = context;
    const properties = node.rawProperties;
    // `_set_setting_count` refuses a negative count and the validator reports it. A count that is
    // absent, unparseable or negative allocates nothing, and Godot's own loop over `sp_settings` runs
    // zero times.
    const count = ruleCount(properties['setting_count'] ?? '');
    if (count === null || count <= 0)
        return [];
    // Grouped by the setting `_set` resolves each key to, not by the index text: `_set` reads the
    // index with a bare `path.get_slicec('/', 1).to_int()` and no validity gate
    // (spline_ik_3d.cpp:37), so `settings/00/path_3d` sets setting 0's path.
    const settings = indexedElements(properties, 'settings/', 'to_int', resolveSplineSettingLeaf);
    const posed = new Set();
    for (const [index, leaves] of settings) {
        if (index >= count)
            continue;
        const path = leaves.get('path_3d');
        if (path !== undefined && !isUnsetPath(path.trim()))
            posed.add(index);
    }
    // Capped at both ends: `setting_count` has no ceiling, so the walk is bounded as well as the
    // message. See `reportedIndices.ts`.
    const { listed: missing, total } = unsatisfiedIndices(count, posed);
    const omitted = total - missing.length;
    const diagnostics = missing.map((index) => armDiagnostic(arms.settingWithoutPath3D, node, `SplineIK3D '${node.name}' setting ${index} has no Path3D. Godot resolves 'settings/${index}/path_3d' before it reads the curve and skips the setting when nothing comes back, so this chain of bones is never posed.`));
    if (omitted > 0) {
        reportArm(diagnostics, arms.settingWithoutPath3D, node, `SplineIK3D '${node.name}' has ${omitted.toLocaleString('en-US')} further settings with no Path3D, not listed individually.`);
    }
    return diagnostics;
}
const splineIK3DPathRule = {
    meta: {
        name: 'valid-splineik3d-path-3d',
        description: 'Warns when a SplineIK3D setting names no Path3D, the configuration Godot itself flags and then skips',
        category: 'validation',
        applicableNodeTypeMatcher: (nodeType) => descendsFromClass(nodeType, 'SplineIK3D'),
        emits: armEmits(arms),
    },
    check: checkSplineIK3D,
};
ruleRegistry.register(splineIK3DPathRule);
//# sourceMappingURL=linter.js.map